import { inject } from 'vue'
import type { App } from 'vue'
import type { ApiAxiosError, ApiErrorResponse } from '@/types/apiAxios'
import { isEmpty } from '@utils/checkers'
import { humanize } from '@utils/formats'
import { isString, isArray, isObject } from '@/utils/checkers/validation'
import forEach from '@/utils/forEach'
import {
  isApiAxiosError,
  isApiErrorResponse,
} from '@/utils/helpers/isApiAxiosError'
import { escapeHtmlString } from '@/components/ContentManager/EditorPage/PageHistory/helpers/textDiff'

export type NotifyOptions = {
  title: string
  link?: string
  linkText?: string
  preventRedirect?: boolean
  humanizeKey?: boolean
}

export type SbNotify = {
  success: (message: string, group?: string, _options?: NotifyOptions) => void
  error: (
    error: string | ApiAxiosError,
    group?: string,
    _options?: NotifyOptions,
  ) => void
  info: (message: string, group?: string, _options?: NotifyOptions) => void
}

export const SbNotifyInjectionKey = Symbol()

export default {
  install: function (app: App): void {
    const sbNotify: SbNotify = {
      success(
        message: string,
        group = 'app',
        _options: NotifyOptions = { title: 'Success', link: '', linkText: '' },
      ): void {
        app.config.globalProperties.$notify({
          group,
          clean: true,
        })

        return app.config.globalProperties.$notify({
          group,
          title: _options.title,
          text: escapeHtmlString(message),
          type: 'success',
          duration: 2500,
          data: {
            link: _options.link,
            linkText: _options.link ? _options.linkText : '',
          },
        })
      },

      error(
        error,
        group = 'app',
        _options = {
          title: 'Oops, there were some problems',
          link: '',
          linkText: '',
          preventRedirect: false,
          humanizeKey: true,
        },
      ): void {
        app.config.globalProperties.$notify({
          group,
          clean: true,
        })

        const response =
          error && typeof error === 'object' ? error.response : undefined
        let message = ''

        if (
          response &&
          response.status === 401 &&
          !_options.preventRedirect &&
          typeof window?.location !== 'undefined'
        ) {
          if (
            response.data.error ===
            'You have to confirm your email address before continuing.'
          ) {
            _options.link = '/confirm/resend'
            _options.linkText = 'Resend confirmation'
          } else {
            window.location.href = window.location.origin
          }
        }

        const getKey = function (
          key: string | number,
          humanizeKey: boolean = true,
        ): string {
          if (key === 'error' || typeof key === 'number' || isEmpty(key)) {
            return ''
          }

          if (!humanizeKey) {
            return `<span class="custom-notification__key">${escapeHtmlString(key)}</span>: `
          }

          return `<span class="custom-notification__key">${escapeHtmlString(
            humanize(key),
          )}</span>: `
        }

        if (response?.data?.message) {
          message = '<li>' + response.data.message + '</li>'
        }

        // error messages for array and object
        const responseData = response?.data
        if (isArray(responseData) && !isEmpty(responseData)) {
          responseData.forEach((errorMessage, key) => {
            if (isArray(errorMessage)) {
              forEach(errorMessage, function (msg) {
                const body = isString(msg) ? msg : ''
                message =
                  message +
                  '<li>' +
                  getKey(key, _options.humanizeKey) +
                  escapeHtmlString(body) +
                  '</li>'
              })
            } else {
              const body = isString(errorMessage) ? errorMessage : ''
              message =
                message +
                '<li>' +
                getKey(key, _options.humanizeKey) +
                escapeHtmlString(body) +
                '</li>'
            }
          })
        } else if (isObject(responseData) && !isEmpty(responseData)) {
          const getMessagesFromObject = (data: ApiErrorResponse): void => {
            Object.entries(data).forEach(([key, errorMessage]) => {
              if (isString(errorMessage)) {
                message +=
                  '<li>' +
                  getKey(key, _options.humanizeKey) +
                  escapeHtmlString(errorMessage) +
                  '</li>'
              } else if (isArray(errorMessage)) {
                errorMessage.map((singleMessage) => {
                  const body = isString(singleMessage) ? singleMessage : ''
                  message +=
                    '<li>' +
                    getKey(key, _options.humanizeKey) +
                    escapeHtmlString(body) +
                    '</li>'
                })
              } else if (isApiErrorResponse(errorMessage)) {
                getMessagesFromObject(errorMessage)
              }
            })
          }

          getMessagesFromObject(responseData)
        }

        if (typeof error === 'string') {
          message = '<li>' + escapeHtmlString(error) + '</li>'
        }

        if (isApiAxiosError(error) && error.status === 0) {
          message =
            '<li>Connection failed. Please check your internet connection and reload the window.</li>'
        }

        message = `<ul>${message}</ul>`

        return app.config.globalProperties.$notify({
          group,
          title: _options.title,
          text: message,
          type: 'error',
          duration: 5000,
          data: {
            link: _options.link,
            linkText: _options.linkText ? _options.linkText : '',
          },
        })
      },

      info(
        message: string,
        group = 'app',
        _options = { title: 'Info', link: '', linkText: '' },
      ): void {
        app.config.globalProperties.$notify({
          group,
          clean: true,
        })

        return app.config.globalProperties.$notify({
          group,
          title: _options.title,
          text: escapeHtmlString(message),
          type: 'info',
          duration: 5000,
          data: {
            link: _options.link,
            linkText: _options.link ? _options.linkText : '',
          },
        })
      },
    }

    app.config.globalProperties.$sbnotify = sbNotify
    app.provide(SbNotifyInjectionKey, sbNotify)
  },
}

export function useSbNotify(): SbNotify {
  return inject(SbNotifyInjectionKey)!
}
